import type { WidgetId } from '@/types/analytics';

/**
 * Widget Content Types
 *
 * Categorizes widgets by their content characteristics to determine
 * optimal column span across different breakpoints.
 */
export type WidgetContentType =
  | 'metric'           // Simple metrics (single stat + visualization)
  | 'chart-bar'        // Bar charts (need horizontal space)
  | 'chart-line'       // Line/trend charts (need horizontal space for timeline)
  | 'chart-radial'     // Radial/circular charts (compact, self-contained)
  | 'list'             // Scrollable lists of items
  | 'heatmap'          // Calendar/data heatmaps (need maximum width)
  | 'complex';         // Multi-component widgets

/**
 * Widget Span Configuration
 *
 * Defines how many grid columns a widget should span at each breakpoint.
 * - mobile: Always 1 (single column layout)
 * - tablet: 1-2 columns (2-column grid)
 * - desktop: 1-3 columns (3-column grid)
 */
export type WidgetSpanConfig = {
  mobile: 1;
  tablet: 1 | 2;
  desktop: 1 | 2 | 3 | 4;
};

/**
 * Widget Metadata
 *
 * Complete metadata for a dashboard widget including content type,
 * responsive span configuration, and optional sizing constraints.
 */
export interface WidgetMetadata {
  id: WidgetId;
  contentType: WidgetContentType;
  defaultSpan: WidgetSpanConfig;
  minWidth?: number;      // Minimum pixel width for proper display
  aspectRatio?: number;   // Preferred aspect ratio (width/height)
}

/**
 * Default Span Configurations by Content Type
 *
 * Automatic column span assignments based on widget content type.
 * These defaults can be overridden per-widget in the registry.
 */
const CONTENT_TYPE_DEFAULTS: Record<WidgetContentType, WidgetSpanConfig> = {
  'metric': {
    mobile: 1,
    tablet: 1,
    desktop: 1,  // Compact metrics can sit side-by-side (1/4 width)
  },
  'chart-bar': {
    mobile: 1,
    tablet: 2,
    desktop: 2,  // Bar charts need horizontal space (1/2 width)
  },
  'chart-line': {
    mobile: 1,
    tablet: 2,
    desktop: 2,  // Line charts need horizontal space for timeline (1/2 width)
  },
  'chart-radial': {
    mobile: 1,
    tablet: 1,
    desktop: 1,  // Circular displays are self-contained (1/4 width)
  },
  'list': {
    mobile: 1,
    tablet: 2,
    desktop: 2,  // Lists benefit from width for readability (1/2 width)
  },
  'heatmap': {
    mobile: 1,
    tablet: 2,
    desktop: 4,  // Heatmaps need maximum width (Full width)
  },
  'complex': {
    mobile: 1,
    tablet: 2,
    desktop: 2,  // Multi-component widgets get medium width (1/2 width)
  },
};

/**
 * Widget Registry
 *
 * Central source of truth for all dashboard widgets. Maps each widget ID
 * to its metadata including content type and responsive span configuration.
 *
 * Add new widgets here to automatically configure their layout behavior.
 */
export const WIDGET_REGISTRY: Record<WidgetId, WidgetMetadata> = {
  // ============================================================================
  // Performance Widgets
  // ============================================================================

  'overall-success-rate': {
    id: 'overall-success-rate',
    contentType: 'chart-radial',
    defaultSpan: CONTENT_TYPE_DEFAULTS['chart-radial'],
    minWidth: 280,
  },

  'performance-by-difficulty': {
    id: 'performance-by-difficulty',
    contentType: 'chart-bar',
    defaultSpan: CONTENT_TYPE_DEFAULTS['chart-bar'],
    minWidth: 400,
  },

  'performance-by-type': {
    id: 'performance-by-type',
    contentType: 'chart-bar',
    defaultSpan: CONTENT_TYPE_DEFAULTS['chart-bar'],
    minWidth: 400,
  },

  'time-efficiency': {
    id: 'time-efficiency',
    contentType: 'chart-bar',
    defaultSpan: CONTENT_TYPE_DEFAULTS['chart-bar'],
    minWidth: 400,
  },

  'time-optimization': {
    id: 'time-optimization',
    contentType: 'chart-bar',
    defaultSpan: CONTENT_TYPE_DEFAULTS['chart-bar'],
    minWidth: 400,
  },

  'partial-credit-analysis': {
    id: 'partial-credit-analysis',
    contentType: 'complex',
    defaultSpan: CONTENT_TYPE_DEFAULTS['complex'],
    minWidth: 380,
  },

  'improvement-trends': {
    id: 'improvement-trends',
    contentType: 'chart-line',
    defaultSpan: {
      mobile: 1,
      tablet: 2,
      desktop: 3,
    },
    minWidth: 450,
  },

  'session-completion-rate': {
    id: 'session-completion-rate',
    contentType: 'metric',
    defaultSpan: CONTENT_TYPE_DEFAULTS['metric'],
    minWidth: 240,
  },

  // ============================================================================
  // Question Widgets
  // ============================================================================

  'most-challenging': {
    id: 'most-challenging',
    contentType: 'list',
    defaultSpan: CONTENT_TYPE_DEFAULTS['list'],
    minWidth: 380,
  },

  'questions-mastered': {
    id: 'questions-mastered',
    contentType: 'list',
    // Override: Simple mastered list can be compact on desktop
    defaultSpan: {
      mobile: 1,
      tablet: 2,
      desktop: 2,
    },
    minWidth: 280,
  },

  'questions-needing-review': {
    id: 'questions-needing-review',
    contentType: 'list',
    defaultSpan: CONTENT_TYPE_DEFAULTS['list'],
    minWidth: 350,
  },

  'time-distribution': {
    id: 'time-distribution',
    contentType: 'chart-bar',
    defaultSpan: {
      mobile: 1,
      tablet: 2,
      desktop: 3,
    },
    minWidth: 400,
  },

  'participation-trends': {
    id: 'participation-trends',
    contentType: 'chart-line',
    defaultSpan: {
      mobile: 1,
      tablet: 2,
      desktop: 2,
    },
    minWidth: 450,
  },

  // ============================================================================
  // Quizlet Widgets
  // ============================================================================

  'time-per-question': {
    id: 'time-per-question',
    contentType: 'chart-bar',
    defaultSpan: CONTENT_TYPE_DEFAULTS['chart-bar'],
    minWidth: 400,
  },

  'question-type-preference': {
    id: 'question-type-preference',
    contentType: 'chart-bar',
    defaultSpan: CONTENT_TYPE_DEFAULTS['chart-bar'],
    minWidth: 400,
  },

  'quizlet-mastery-heatmap': {
    id: 'quizlet-mastery-heatmap',
    contentType: 'heatmap',
    defaultSpan: {
      mobile: 1,
      tablet: 2,
      desktop: 2,
    },
    minWidth: 400,
  },

  'practice-frequency': {
    id: 'practice-frequency',
    contentType: 'chart-bar',
    defaultSpan: CONTENT_TYPE_DEFAULTS['chart-bar'],
    minWidth: 400,
  },

  'best-worst-performing': {
    id: 'best-worst-performing',
    contentType: 'complex',
    defaultSpan: CONTENT_TYPE_DEFAULTS['complex'],
    minWidth: 380,
  },

  'tag-based-mastery': {
    id: 'tag-based-mastery',
    contentType: 'chart-bar',
    defaultSpan: CONTENT_TYPE_DEFAULTS['chart-bar'],
    minWidth: 400,
  },

  'topic-mastery': {
    id: 'topic-mastery',
    contentType: 'chart-bar',
    defaultSpan: CONTENT_TYPE_DEFAULTS['chart-bar'],
    minWidth: 400,
  },

  // ============================================================================
  // Engagement Widgets
  // ============================================================================

  'daily-knowledge-streak': {
    id: 'daily-knowledge-streak',
    contentType: 'metric',
    defaultSpan: CONTENT_TYPE_DEFAULTS['metric'],
    minWidth: 240,
  },

  'total-questions-answered': {
    id: 'total-questions-answered',
    contentType: 'metric',
    defaultSpan: CONTENT_TYPE_DEFAULTS['metric'],
    minWidth: 240,
  },

  'total-study-time': {
    id: 'total-study-time',
    contentType: 'metric',
    defaultSpan: CONTENT_TYPE_DEFAULTS['metric'],
    minWidth: 240,
  },

  'sessions-completed': {
    id: 'sessions-completed',
    contentType: 'metric',
    defaultSpan: CONTENT_TYPE_DEFAULTS['metric'],
    minWidth: 240,
  },

  'calendar-heatmap': {
    id: 'calendar-heatmap',
    contentType: 'heatmap',
    defaultSpan: {
      mobile: 1,
      tablet: 2,
      desktop: 3,
    },
    minWidth: 600,
  },

  // ============================================================================
  // Insights Widgets
  // ============================================================================

  'automated-insights': {
    id: 'automated-insights',
    contentType: 'list',
    defaultSpan: CONTENT_TYPE_DEFAULTS['list'],
    minWidth: 360,
  },

  'practice-recommendations': {
    id: 'practice-recommendations',
    contentType: 'list',
    defaultSpan: CONTENT_TYPE_DEFAULTS['list'],
    minWidth: 360,
  },

  'best-study-time': {
    id: 'best-study-time',
    contentType: 'complex',
    defaultSpan: CONTENT_TYPE_DEFAULTS['complex'],
    minWidth: 400,
  },

  'learning-curve': {
    id: 'learning-curve',
    contentType: 'chart-line',
    defaultSpan: CONTENT_TYPE_DEFAULTS['chart-line'],
    minWidth: 450,
  },

  // ============================================================================
  // Article Widgets
  // ============================================================================

  'article-stats': {
    id: 'article-stats',
    contentType: 'metric',
    defaultSpan: CONTENT_TYPE_DEFAULTS['metric'],
    minWidth: 240,
  },

  'article-views-chart': {
    id: 'article-views-chart',
    contentType: 'chart-line',
    defaultSpan: {
      mobile: 1,
      tablet: 2,
      desktop: 2,
    },
    minWidth: 400,
  },

  'top-articles': {
    id: 'top-articles',
    contentType: 'list',
    defaultSpan: CONTENT_TYPE_DEFAULTS['list'],
    minWidth: 380,
  },
};

/**
 * Get Widget Span for Current Breakpoint
 *
 * Returns the number of columns a widget should span at the given breakpoint.
 *
 * @param widgetId - The unique identifier for the widget
 * @param breakpoint - Current viewport breakpoint ('mobile' | 'tablet' | 'desktop')
 * @returns Number of grid columns to span (1-3)
 *
 * @example
 * ```ts
 * const span = getWidgetSpan('performance-by-difficulty', 'desktop'); // Returns 2
 * ```
 */
export function getWidgetSpan(
  widgetId: WidgetId,
  breakpoint: 'mobile' | 'tablet' | 'desktop'
): number {
  const metadata = WIDGET_REGISTRY[widgetId];
  return metadata?.defaultSpan[breakpoint] || 1;
}

/**
 * Get Widget Metadata
 *
 * Returns complete metadata for a widget including content type,
 * span configuration, and sizing constraints.
 *
 * @param widgetId - The unique identifier for the widget
 * @returns Widget metadata or undefined if widget not found
 *
 * @example
 * ```ts
 * const metadata = getWidgetMetadata('improvement-trends');
 * console.log(metadata.contentType); // 'chart-line'
 * console.log(metadata.minWidth);    // 450
 * ```
 */
export function getWidgetMetadata(widgetId: WidgetId): WidgetMetadata | undefined {
  return WIDGET_REGISTRY[widgetId];
}

/**
 * Check if Widget Needs Wide Layout
 *
 * Helper function to determine if a widget requires 2+ columns on desktop.
 * Useful for conditional styling or layout decisions.
 *
 * @param widgetId - The unique identifier for the widget
 * @returns true if widget spans 2+ columns on desktop
 *
 * @example
 * ```ts
 * if (isWideWidget('performance-by-difficulty')) {
 *   // Apply special wide-widget styling
 * }
 * ```
 */
export function isWideWidget(widgetId: WidgetId): boolean {
  const metadata = WIDGET_REGISTRY[widgetId];
  return (metadata?.defaultSpan.desktop || 1) >= 2;
}

/**
 * Get All Widgets by Content Type
 *
 * Returns array of widget IDs that match the specified content type.
 * Useful for batch operations or filtering.
 *
 * @param contentType - The content type to filter by
 * @returns Array of widget IDs matching the content type
 *
 * @example
 * ```ts
 * const chartWidgets = getWidgetsByContentType('chart-bar');
 * // ['performance-by-difficulty', 'performance-by-type', ...]
 * ```
 */
export function getWidgetsByContentType(contentType: WidgetContentType): WidgetId[] {
  return Object.values(WIDGET_REGISTRY)
    .filter(metadata => metadata.contentType === contentType)
    .map(metadata => metadata.id);
}
